import { filter } from 'obliterator';
import { Frustum, Raycaster, Vector3, Group } from 'three';
import type {
  Box3,
  Camera,
  Intersection,
  Object3D,
  Float32BufferAttribute,
  Vector2,
} from 'three';

import { ENTITY_TYPES, PART_VIEWER_MODES, SPLIT_CHAR } from '../constants';
import type { PART_TYPES } from '../constants';
import type { OBBSimplifiedRepresentation } from '../loaders/OBBSimplifiedRepresentation';
import type { Body, AnyBody } from '../loaders/body';
import type { Part } from '../loaders/part';
import { StepEdgeModel } from '../loaders/step/StepEdgeModel';
import { StepFaceModel } from '../loaders/step/StepFaceModel';
import { StepTextModel } from '../loaders/step/StepTextModel';
import type { StepVertexBody } from '../loaders/step/StepVertexBody';
import { STLBody } from '../loaders/stl/STLBody';
import { maxSTLVertices } from '../loaders/stl/STLGeometry';
import type {
  Intersectable,
  MouseMarker,
  Renderer,
} from '../renderer/renderer';

const INTERSECTION_TOLERANCES = {
  face: 4,
  edge: 1.5,
};

export type IntersectionFace = Required<Intersection<Intersectable>>['face'] & {
  vertices?: [Vector3, Vector3, Vector3];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyIntersectionTest = IntersectionTest<any>;

//IntersectionObject is always an Intersectable
export type IntersectionObject<T extends PART_TYPES> =
  | (T extends PART_TYPES.STEP | PART_TYPES.DXF
      ?
          | StepVertexBody
          | StepEdgeModel
          | StepFaceModel
          | StepTextModel
          | OBBSimplifiedRepresentation
      : T extends PART_TYPES.STL
      ? Body<PART_TYPES.STL>['obj']['meshes'][0] | OBBSimplifiedRepresentation //STL's fullDetailRep is its mesh (wireframe not included)
      : never)
  | null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyIntersectionObject = IntersectionObject<any>;

export type IntersectionTest<T extends PART_TYPES> = {
  body: string; //bodyId
  type: ENTITY_TYPES;
  //TODO: Why obj? and not just object? (seems that frontend's callbacks sometimes look for obj, and others look for object)
  //partviewer could eliminate obj and use just object. But I have not investigated effort for rest of frontend
  obj?: IntersectionObject<T>;
  object?: IntersectionObject<T>;
  point?: Vector3 | null;
  face?: null | IntersectionFace;
  normal?: Vector3;
  part?: Part<T>;
};

export const EMPTY_INTERSECTION_OBJECT: IntersectionTest<PART_TYPES> = {
  body: '',
  type: ENTITY_TYPES.FACE,
};

const ascSort = (a: { distance: number }, b: { distance: number }) =>
  a.distance - b.distance;

//PPRaycaster extends Raycaster and provides a more optimal intersectObjects for our use case
class PPRaycaster extends Raycaster {
  //Similar implementation as Raycaster, however it allows `objects` to be iterable,
  //which improves performance because iteration is reduced
  //* Also doesn't use Raycaster's private intersectObject (which is recursive) and instead inlines the code and
  //  uses a stack to reduce function calls.
  public intersectObjects<TIntersected extends Object3D>(
    objects: Iterable<Object3D>,
    recursive?: boolean,
    intersects: Intersection<TIntersected>[] = [],
  ): Array<Intersection<TIntersected>> {
    if (recursive) {
      //Only iterate objects once, and use stack
      for (const object of objects) {
        const stack = [object];
        let o: Object3D | undefined;
        //eslint-disable-next-line no-cond-assign
        while ((o = stack.pop())) {
          if (o.layers.test(this.layers)) {
            o.raycast(this, intersects);
          }
          stack.push(...o.children);
        }
      }
    } else {
      for (const object of objects) {
        if (object.layers.test(this.layers)) {
          object.raycast(this, intersects);
        }
      }
    }
    intersects.sort(ascSort);
    return intersects;
  }
}

const isVisible = (intersectable: Intersectable): boolean => {
  let { visible, parent } = intersectable;
  //using while loop rather than recursion for performance, and isVisible argument can be `Renderable` which does not include `Group`.
  while (visible && parent instanceof Group) {
    visible = parent.visible;
    parent = parent.parent;
  }
  return visible;
};

const createFrustumFromMousePoints = (
  startPoint: MouseMarker,
  endPoint: MouseMarker,
  camera: Camera,
): Frustum => {
  // https://threejs.org/examples/misc_boxselection.html
  const left = Math.min(startPoint.x, endPoint.x);
  const top = Math.max(startPoint.y, endPoint.y);
  const right = Math.max(startPoint.x, endPoint.x);
  const down = Math.min(startPoint.y, endPoint.y);

  const vecTopLeft = new Vector3(left, top, -1);
  const vecTopRight = new Vector3(right, top, -1);
  const vecDownRight = new Vector3(right, down, -1);
  const vecDownLeft = new Vector3(left, down, -1);

  const vecFarTopLeft = new Vector3(left, top, 1);
  const vecFarTopRight = new Vector3(right, top, 1);
  const vecFarDownRight = new Vector3(right, down, 1);
  const vecFarDownLeft = new Vector3(left, down, 1);

  vecTopLeft.unproject(camera);
  vecTopRight.unproject(camera);
  vecDownRight.unproject(camera);
  vecDownLeft.unproject(camera);

  vecFarTopLeft.unproject(camera);
  vecFarTopRight.unproject(camera);
  vecFarDownRight.unproject(camera);
  vecFarDownLeft.unproject(camera);

  const frustum = new Frustum();
  const planes = frustum.planes;

  // top plane
  planes[0].setFromCoplanarPoints(vecTopLeft, vecFarTopLeft, vecFarTopRight);
  // right plane
  planes[1].setFromCoplanarPoints(vecTopRight, vecFarTopRight, vecFarDownRight);
  // bottom plane
  planes[2].setFromCoplanarPoints(vecFarDownRight, vecFarDownLeft, vecDownLeft);
  // left plane
  planes[3].setFromCoplanarPoints(vecFarDownLeft, vecFarTopLeft, vecTopLeft);
  // front plane
  planes[4].setFromCoplanarPoints(vecTopRight, vecDownRight, vecDownLeft);
  // back plane
  planes[5].setFromCoplanarPoints(
    vecFarDownRight,
    vecFarTopRight,
    vecFarTopLeft,
  );
  planes[5].normal.multiplyScalar(-1);

  return frustum;
};

const doesFrustumContainBox = (
  frustum: Frustum,
  { min, max }: Box3,
): boolean => {
  const containsBox =
    frustum.containsPoint(min) &&
    frustum.containsPoint(new Vector3(min.x, min.y, max.z)) &&
    frustum.containsPoint(new Vector3(min.x, max.y, min.z)) &&
    frustum.containsPoint(new Vector3(min.x, max.y, max.z)) &&
    frustum.containsPoint(new Vector3(max.x, min.y, min.z)) &&
    frustum.containsPoint(new Vector3(max.x, min.y, max.z)) &&
    frustum.containsPoint(new Vector3(max.x, max.y, min.z)) &&
    frustum.containsPoint(max);
  return containsBox;
};

/*
Will perform an intersection check based on the created frustum projection from a box
drawn on the screen. This performs an entirely inclusive check, by looking to see if
all corners of each entity's bounding box fall within the frustum.
It will perform an additional check of the projected shape bounding box from its
tessellation if the bounding box check does not yield success but there is still
some overlap.
*/
export const handleBoxSelectTotalInclusion = (
  startPoint: MouseMarker,
  endPoint: MouseMarker,
  renderer: Renderer,
): AnyIntersectionTest[] => {
  const frustum = createFrustumFromMousePoints(
    startPoint,
    endPoint,
    renderer.camera,
  );

  const { bodyOrAssemblyInstanceById, bodies } = renderer.state;

  // NOTE: we leave off vertices so they are not included in intersections
  // turn on vertexes so they are included in intersections
  for (const body of bodies) {
    if (!body.isLoaded || body.obj instanceof STLBody) continue;
    for (const vertex of body.obj.vertices.values()) {
      vertex.renderable.visible = false;
    }
  }

  const intersected: AnyIntersectionTest[] = []; //Note: type is T, but PART_TYPES_STL are not accumulated
  for (const obj of renderer.intersectionEntities) {
    if (!isVisible(obj)) {
      continue;
    }
    const [bodyId, entityType] = obj.name.split(SPLIT_CHAR) as [
      string,
      (
        | ENTITY_TYPES.FACE
        | ENTITY_TYPES.EDGE
        | ENTITY_TYPES.VERTEX
        | ENTITY_TYPES.TEXT
        | ENTITY_TYPES.STL
        | ENTITY_TYPES.SimpRep
      ),
    ];

    if (
      !bodyId ||
      entityType === ENTITY_TYPES.SimpRep ||
      entityType === ENTITY_TYPES.STL
    )
      continue;
    const boundBox = obj.boundBox; //StepVertexBody does not have boundBox on its renderable!
    if (!boundBox) continue;

    if (frustum.intersectsBox(boundBox)) {
      if (doesFrustumContainBox(frustum, boundBox)) {
        const body = bodyOrAssemblyInstanceById.get(
          bodyId,
        ) as Body<PART_TYPES.STEP>; //STL types are skipped above
        const object = body.obj
          .getObjsByType(entityType)
          ?.get(obj.name) as IntersectionTest<PART_TYPES.STEP>['object'];
        intersected.push({
          body: bodyId,
          type: entityType,
          obj: object,
          object,
          face: null,
          point: null,
          part: body.part,
        });
      } else if (renderer.state.viewerMode !== PART_VIEWER_MODES.VECTOR_EDIT) {
        // loop thru all vertex points, and if any do not fall inside box, throw it out
        let hasOutsidePoint = false;
        if (
          obj instanceof StepEdgeModel ||
          obj instanceof StepFaceModel ||
          obj instanceof StepTextModel
        ) {
          const positionVector = new Vector3();
          let i = 0;
          for (const position of obj.positions()) {
            const xyzIndex = i % 3;
            if (xyzIndex === 0) {
              positionVector.setX(position);
            } else if (xyzIndex === 1) {
              positionVector.setY(position);
            } else if (xyzIndex === 2) {
              positionVector.setZ(position);
              const wc = obj.localToWorld(positionVector);
              if (!frustum.containsPoint(wc)) {
                hasOutsidePoint = true;
                break;
              }
            }
            i++;
          }
          // Empty / missing host geometry (e.g. some DXF paths): cannot verify inclusion
          if (i === 0) {
            hasOutsidePoint = true;
          }
        } else {
          const position = obj.geometry?.attributes?.position as
            | Float32BufferAttribute
            | undefined;
          if (!position) {
            hasOutsidePoint = true;
          } else {
            for (let j = 0; j < position.count; j++) {
              // get world coordinate of vertice
              const wc = obj.localToWorld(
                new Vector3().fromBufferAttribute(position, j),
              );
              if (!frustum.containsPoint(wc)) {
                hasOutsidePoint = true;
                break;
              }
            }
          }
        }
        if (!hasOutsidePoint) {
          const body = bodyOrAssemblyInstanceById.get(
            bodyId,
          ) as Body<PART_TYPES.STEP>; //STL types are skipped above
          const object = body.obj
            .getObjsByType(entityType)
            ?.get(obj.name) as IntersectionTest<PART_TYPES.STEP>['object'];
          intersected.push({
            body: bodyId,
            type: entityType,
            obj: object,
            object,
            face: null,
            point: null,
            part: body.part,
          });
        }
      }
    }
  }

  return intersected;
};

const performRaycasterObjectIntersect = (
  mousePosition: MouseMarker,
  renderer: Renderer,
): Intersection<Object3D>[] => {
  const { bodies } = renderer.state;

  // turn on vertexes so they are included in intersections
  const previouslyInvisibleVertexes = [];
  for (const body of bodies) {
    if (!body.isLoaded || body.obj instanceof STLBody) continue;
    for (const vertex of (
      body as Body<PART_TYPES.STEP>
    ).obj.vertices.values()) {
      if (!vertex.renderable.visible) {
        previouslyInvisibleVertexes.push(vertex);
        vertex.renderable.visible = true;
      }
    }
  }

  // Tolerances more lenient towards edge and vertex selection
  const raycaster = new PPRaycaster();
  (raycaster.params.Line as { threshold: number }).threshold =
    renderer.scaleRadius / INTERSECTION_TOLERANCES.edge;
  raycaster.params.Mesh.threshold =
    renderer.scaleRadius / INTERSECTION_TOLERANCES.face;

  raycaster.setFromCamera(mousePosition as Vector2, renderer.camera);

  // need to only do intersection checks on objects that are visible
  // we need to traverse up parents to see if objects are visible
  // TODO: is it more performant to go top down?
  // returns intersections sorted by distance to raycaster point
  const intersections: Intersection<Object3D>[] = raycaster.intersectObjects(
    filter(renderer.intersectionEntities, isVisible),
    false,
  );

  // turn off vertexes so they are hidden again
  for (const vertex of previouslyInvisibleVertexes) {
    vertex.renderable.visible = false;
  }

  return intersections;
};

/**
Performs ray intersection logic that dictates hover and selection. Includes edge buffering so that edges and vertexes
are relatively easier to highlight with the cursor compared to faces and meshes.
Returns a single object of the intersection nearest to the ray casting source point.
*/
export const handleFirstObjectIntersect = (
  mousePosition: MouseMarker,
  renderer: Renderer,
): AnyIntersectionTest => {
  const intersections: Intersection<Object3D>[] =
    performRaycasterObjectIntersect(mousePosition, renderer);

  const { bodyOrAssemblyInstanceById } = renderer.state;

  //First intersection:
  //* intersections are sorted by their distance.  If there is a tie for the shortest distance, we choose the last tied intersection
  //* candidate is the candidate 'first' intersection. We continue to iterate to verify there is no tie. If there is a tie, we update the candidate
  //  to be the last tied intersection
  //* Ties tend not to occur with STEP files... but with DXF files, were there entities are in the same plane, there can be ties with overlapping edges
  //  When there is a tie we want the last tied intersection.
  let lastDistance: number | undefined;
  let candidate: AnyIntersectionTest | undefined;
  for (const intersection of intersections) {
    const distance = intersection.distance;
    if (candidate && lastDistance !== distance) {
      return candidate;
    }
    lastDistance = intersection.distance;

    // collect object name and navigate based on name composition
    const [bodyId, entityType, geometriesIndexAsString] =
      intersection.object.name.split(SPLIT_CHAR) as [
        string,
        ENTITY_TYPES,
        string?,
      ];
    if (bodyId) {
      const body = bodyOrAssemblyInstanceById.get(bodyId) as AnyBody;
      if (
        entityType === ENTITY_TYPES.EDGE ||
        entityType === ENTITY_TYPES.VERTEX ||
        entityType === ENTITY_TYPES.TEXT
      ) {
        const object = (body as Body<PART_TYPES.STEP>).obj
          .getObjsByType(entityType)
          ?.get(
            intersection.object.name,
          ) as IntersectionTest<PART_TYPES.STEP>['object'];
        candidate = {
          body: bodyId,
          type: entityType,
          object,
          point: intersection.point,
          part: body.part,
        };
      } else if (entityType === ENTITY_TYPES.SimpRep) {
        candidate = {
          body: bodyId,
          type: entityType,
          object: body.obbSimplifiedRep,
          point: intersection.point,
          part: body.part,
        };
      } else {
        //otherwise it is ENTITY_TYPES.FACE | ENTITY_TYPES.STL
        let obj: AnyIntersectionTest['object'];
        const face = intersection.face as IntersectionFace;
        if (entityType === ENTITY_TYPES.STL) {
          const geometriesIndex = parseInt(geometriesIndexAsString ?? '0', 10);
          obj = (body as Body<PART_TYPES.STL>).obj.meshes[geometriesIndex];
          //STL body has no siblings and is not transformed from part, so can use part to get geometry
          const geometries = (body as Body<PART_TYPES.STL>).part.geometry
            .geometries;
          const positions = geometries[geometriesIndex].attributes
            .position as Float32BufferAttribute;
          //Notes:
          //* face.vertices is used in renderer-component.js. See `const { vertices } = face;`
          //  The vertices are used to calculate `cumulativeInfo`
          //* load-utils.js also sets face.vertices
          face.vertices = [
            new Vector3().fromBufferAttribute(positions, face.a),
            new Vector3().fromBufferAttribute(positions, face.b),
            new Vector3().fromBufferAttribute(positions, face.c),
          ];
          //After extracting the 3 vectors for the vertices, update encoding of vertex indices to be across the set of STL bufferGeometries
          face.a = face.a + geometriesIndex * maxSTLVertices;
          face.b = face.b + geometriesIndex * maxSTLVertices;
          face.c = face.c + geometriesIndex * maxSTLVertices;
        } else {
          obj = (body as Body<PART_TYPES.STEP>).obj
            .getObjsByType(entityType)
            ?.get(intersection.object.name) as StepFaceModel;
        }
        //face.normal may not be Vector3. When not, Vector3 must be constructed. Otherwise we still
        //construct it because we want to clone it.
        const normalUse =
          face && new Vector3(face.normal.x, face.normal.y, face.normal.z);
        if (normalUse) {
          normalUse.transformDirection(obj.matrixWorld);
        }
        candidate = {
          body: bodyId,
          type: entityType,
          object: obj,
          point: intersection.point,
          face,
          normal: normalUse,
          part: body.part,
        };
      }
    }
  }

  // If there is just 1 intersection, or all intersections tied, then the loop finishes with a `candidate` that we should return
  // if no collision has yielded, return a pseudo-empty result
  return candidate ?? EMPTY_INTERSECTION_OBJECT;
};
